import { makeAutoObservable, observable, runInAction } from "mobx";
import type { TIssueLayout } from "@/types/issue";
import type { TBoardGroupByOption } from "@/types/issue-filter";

const STORAGE_KEY = "work-item-view-preferences";

/**
 * Cross-session persistence for a Work Items view's last-used layout.
 *
 * The in-page source of truth stays the URL (``?layout=…``); this store
 * only remembers a user's last-used layout per **scope** so that a fresh
 * visit with no layout in the URL (sidebar navigation, a bookmark, a new
 * session) can restore it. ``localStorage`` is the right backend here — a
 * last-used layout is a client-only UI preference, not server state — and
 * keeping the read/write in a store (rather than poking ``localStorage``
 * from the component) gives us SSR-safety + error handling in one place,
 * matching ``ModuleListPreferencesStore``.
 *
 * A *scope* is any stable string key identifying the surface. Project
 * work-item lists pass the project id; the workspace-wide Task / Event list
 * pages pass a fixed key (``"workspace:tasks"`` / ``"workspace:events"``).
 * The store treats the key opaquely, so new surfaces only need a unique
 * string — no schema change here.
 *
 * NOTE: "work item" is the product term. The legacy ``issue``/``Issue``
 * naming elsewhere (``TIssueLayout``, ``components/issues``, the ``issue.*``
 * stores) is slated to be renamed to work items as a separate effort; new
 * code uses the work-item name so we stop spreading the old one.
 */
export class WorkItemViewPreferencesStore {
  layoutByScope = observable.map<string, TIssueLayout>();
  /** What the BOARD makes its columns out of, per scope (NG-713). The URL is
   *  still the in-page source of truth (`?board_group_by=`); this remembers the
   *  pick so leaving the board and coming back does not silently drop a stage
   *  board to Status, the way the layout is remembered. */
  boardGroupByByScope = observable.map<string, TBoardGroupByOption>();

  constructor() {
    makeAutoObservable(this, {} as never, { autoBind: true });
    this.hydrate();
  }

  private hydrate(): void {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      // ``layoutByScope`` is the current shape; ``layoutByProject`` is the
      // pre-generalization key, read here so a returning user's stored
      // per-project layouts survive the rename (back-compat).
      const parsed = JSON.parse(raw) as {
        layoutByScope?: Record<string, TIssueLayout>;
        layoutByProject?: Record<string, TIssueLayout>;
        boardGroupByByScope?: Record<string, TBoardGroupByOption>;
      };
      runInAction(() => {
        this.layoutByScope.replace(parsed.layoutByScope ?? parsed.layoutByProject ?? {});
        // Absent for anyone whose prefs predate NG-713 — an empty map, not a
        // reset, so their stored layouts survive the upgrade.
        this.boardGroupByByScope.replace(parsed.boardGroupByByScope ?? {});
      });
    } catch (err) {
      // Corrupt or unreadable (private mode / quota) — start clean.
      console.warn("[work-item-view-prefs] hydrate failed; resetting", err);
      runInAction(() => {
        this.layoutByScope.clear();
        this.boardGroupByByScope.clear();
      });
    }
  }

  private persist(): void {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          layoutByScope: Object.fromEntries(this.layoutByScope),
          boardGroupByByScope: Object.fromEntries(this.boardGroupByByScope),
        }),
      );
    } catch {
      // Best-effort: localStorage can throw in private mode / on quota.
    }
  }

  /** Last-used layout for ``scopeKey`` (a project id or a workspace list key). */
  getLayout(scopeKey: string): TIssueLayout | null {
    return this.layoutByScope.get(scopeKey) ?? null;
  }

  setLayout(scopeKey: string, layout: TIssueLayout): void {
    if (this.layoutByScope.get(scopeKey) === layout) return;
    this.layoutByScope.set(scopeKey, layout);
    this.persist();
  }

  /** Last-used board column dimension for ``scopeKey``. */
  getBoardGroupBy(scopeKey: string): TBoardGroupByOption | null {
    return this.boardGroupByByScope.get(scopeKey) ?? null;
  }

  setBoardGroupBy(scopeKey: string, groupBy: TBoardGroupByOption): void {
    if (this.boardGroupByByScope.get(scopeKey) === groupBy) return;
    this.boardGroupByByScope.set(scopeKey, groupBy);
    this.persist();
  }
}
